import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { upperCaseFirst } from '../../Util/StringUtils';

export interface IFreightContactEntityAttributes extends IModelAttributes {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	companyName: string;
	address: string;
	removed: boolean;

	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
	notifyFreightDetails: Array<Models.FreightDetailEntity | Models.IFreightDetailEntityAttributes>;
	receiverFreightDetails: Array<Models.FreightDetailEntity | Models.IFreightDetailEntityAttributes>;
}
@entity('FreightContactEntity', 'Freight Contact')
export default class FreightContactEntity extends Model implements IFreightContactEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The first name of the freight contact.
	 */
	@observable
	@attribute<FreightContactEntity, string>()
	public firstName: string;

	/**
	 * The last name of the freight contact.
	 */
	@observable
	@attribute<FreightContactEntity, string>()
	public lastName: string;

	/**
	 * The email address of the freight contact.
	 */
	@observable
	@attribute<FreightContactEntity, string>()
	public email: string;

	/**
	 * The phone number of the freight contact.
	 */
	@observable
	@attribute<FreightContactEntity, string>()
	public phone: string;

	/**
	 * The company name for the freight contact.
	 */
	@observable
	@attribute<FreightContactEntity, string>()
	public companyName: string;

	/**
	 * The address of the freight contact.
	 */
	@observable
	@attribute<FreightContactEntity, string>()
	public address: string;

	/**
	 * Whether or not this freight contact is removed from previous option selection.
	 */
	@observable
	@attribute<FreightContactEntity, boolean>()
	public removed: boolean;

	@observable
	@attribute()
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public notifyFreightDetails: Models.FreightDetailEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public receiverFreightDetails: Models.FreightDetailEntity[] = [];

	constructor(attributes?: Partial<IFreightContactEntityAttributes>) {
		super(attributes);

		this.notifyFreightDetails = [];
		this.receiverFreightDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFreightContactEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.firstName !== undefined) {
				this.firstName = attributes.firstName;
			}
			if (attributes.lastName !== undefined) {
				this.lastName = attributes.lastName;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.companyName !== undefined) {
				this.companyName = attributes.companyName;
			}
			if (attributes.address !== undefined) {
				this.address = attributes.address;
			}
			if (attributes.removed !== undefined) {
				this.removed = attributes.removed;
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.notifyFreightDetails !== undefined && Array.isArray(attributes.notifyFreightDetails)) {
				if (!this.notifyFreightDetails) {
					this.notifyFreightDetails = [];
				}
				for (const model of attributes.notifyFreightDetails) {
					if (model instanceof Models.FreightDetailEntity) {
						this.notifyFreightDetails.push(model);
					} else {
						this.notifyFreightDetails.push(new Models.FreightDetailEntity(model));
					}
				}
			}
			if (attributes.receiverFreightDetails !== undefined && Array.isArray(attributes.receiverFreightDetails)) {
				if (!this.receiverFreightDetails) {
					this.receiverFreightDetails = [];
				}
				for (const model of attributes.receiverFreightDetails) {
					if (model instanceof Models.FreightDetailEntity) {
						this.receiverFreightDetails.push(model);
					} else {
						this.receiverFreightDetails.push(new Models.FreightDetailEntity(model));
					}
				}
			}
		}
	}

	public getFullName() {
		return `${upperCaseFirst(this.firstName?.toLowerCase())} ${upperCaseFirst(this.lastName?.toLowerCase())}`;
	}
}
